import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { uploadExcelFile, getJobStatus } from '../services/api';
import { toast } from 'react-toastify';

const UploadExcelPage = () => {
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [jobId, setJobId] = useState(null);
    const [jobStatus, setJobStatus] = useState(null);
    const [polling, setPolling] = useState(false);
    const fileInputRef = useRef(null);
    const navigate = useNavigate();
    const statusIntervalRef = useRef(null);

    useEffect(() => {
        // Cleanup interval on unmount
        return () => {
            if (statusIntervalRef.current) {
                clearInterval(statusIntervalRef.current);
            }
        };
    }, []);

    // Poll for job status
    useEffect(() => {
        if (jobId && polling) {
            const fetchStatus = async () => {
                try {
                    const token = localStorage.getItem('token');
                    const response = await getJobStatus(jobId, token);
                    setJobStatus(response.data);

                    // Stop polling if job is completed or failed
                    if (response.data.status === 'completed' || response.data.status === 'failed') {
                        setPolling(false);
                        if (statusIntervalRef.current) {
                            clearInterval(statusIntervalRef.current);
                        }
                    }
                } catch (error) {
                    console.error('Error fetching job status:', error);
                    setPolling(false);
                    if (statusIntervalRef.current) {
                        clearInterval(statusIntervalRef.current);
                    }
                }
            };

            // Fetch immediately
            fetchStatus();

            // Then poll every 2 seconds
            statusIntervalRef.current = setInterval(fetchStatus, 2000);
        }

        return () => {
            if (statusIntervalRef.current) {
                clearInterval(statusIntervalRef.current);
            }
        };
    }, [jobId, polling]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            const allowedExtensions = ['.xlsx', '.xls'];
            const fileExt = selectedFile.name.toLowerCase().substring(selectedFile.name.lastIndexOf('.'));
            
            if (!allowedExtensions.includes(fileExt)) {
                toast.error('Please upload an Excel file (.xlsx or .xls)');
                return;
            }
            
            setFile(selectedFile);
            setJobId(null);
            setJobStatus(null);
        }
    };

    const handleUpload = async () => {
        if (!file) {
            toast.error('Please select a file to upload');
            return;
        }

        setUploading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await uploadExcelFile(file, token);
            
            setJobId(response.jobId);
            setPolling(true);
            toast.success('File uploaded successfully! Processing started...');
        } catch (error) {
            console.error('Upload error:', error);
            toast.error(error.message || 'Failed to upload file');
        } finally {
            setUploading(false);
        }
    };

    const handleReset = () => {
        setFile(null);
        setJobId(null);
        setJobStatus(null);
        setPolling(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        if (statusIntervalRef.current) {
            clearInterval(statusIntervalRef.current);
        }
    };

    const formatDuration = (ms) => {
        if (!ms) return 'N/A';
        const seconds = Math.floor(ms / 1000);
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}m ${remainingSeconds}s`;
    };

    return (
        <div className="min-h-screen bg-gray-100 py-8">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h1 className="text-3xl font-bold text-gray-900">Upload Excel File</h1>
                        <button
                            onClick={() => navigate('/admin/dashboard')}
                            className="px-4 py-2 text-gray-600 hover:text-gray-900"
                        >
                            ← Back to Dashboard
                        </button>
                    </div>

                    <div className="space-y-6">
                        {/* File Upload Section */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Select Excel File (.xlsx or .xls)
                            </label>
                            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                                <div className="space-y-1 text-center">
                                    <svg
                                        className="mx-auto h-12 w-12 text-gray-400"
                                        stroke="currentColor"
                                        fill="none"
                                        viewBox="0 0 48 48"
                                        aria-hidden="true"
                                    >
                                        <path
                                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                            strokeWidth={2}
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                    <div className="flex text-sm text-gray-600">
                                        <label
                                            htmlFor="file-upload"
                                            className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500"
                                        >
                                            <span>Upload a file</span>
                                            <input
                                                id="file-upload"
                                                name="file-upload"
                                                type="file"
                                                className="sr-only"
                                                accept=".xlsx,.xls"
                                                onChange={handleFileChange}
                                                ref={fileInputRef}
                                            />
                                        </label>
                                        <p className="pl-1">or drag and drop</p>
                                    </div>
                                    <p className="text-xs text-gray-500">Excel files only (.xlsx, .xls)</p>
                                    {file && (
                                        <p className="text-sm text-gray-900 mt-2">
                                            Selected: <span className="font-medium">{file.name}</span>
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Upload Button */}
                        <div className="flex justify-end space-x-4">
                            {file && !jobId && (
                                <button
                                    onClick={handleReset}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                                >
                                    Clear
                                </button>
                            )}
                            <button
                                onClick={handleUpload}
                                disabled={!file || uploading || polling}
                                className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                                {uploading ? 'Uploading...' : 'Upload & Process'}
                            </button>
                        </div>

                        {/* Job Status Section */}
                        {jobId && jobStatus && (
                            <div className="mt-8 border-t border-gray-200 pt-6">
                                <h2 className="text-xl font-semibold text-gray-900 mb-4">Processing Status</h2>
                                
                                <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-gray-700">Status:</span>
                                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                            jobStatus.status === 'completed' ? 'bg-green-100 text-green-800' :
                                            jobStatus.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                                            jobStatus.status === 'failed' ? 'bg-red-100 text-red-800' :
                                            'bg-gray-100 text-gray-800'
                                        }`}>
                                            {jobStatus.status.toUpperCase()}
                                        </span>
                                    </div>

                                    {jobStatus.total > 0 && (
                                        <>
                                            <div className="space-y-2">
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-gray-600">Progress:</span>
                                                    <span className="font-medium">
                                                        {jobStatus.processed} / {jobStatus.total}
                                                    </span>
                                                </div>
                                                <div className="w-full bg-gray-200 rounded-full h-2.5">
                                                    <div
                                                        className="bg-indigo-600 h-2.5 rounded-full transition-all duration-300"
                                                        style={{
                                                            width: `${(jobStatus.processed / jobStatus.total) * 100}%`
                                                        }}
                                                    ></div>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200">
                                                <div>
                                                    <div className="text-2xl font-bold text-green-600">
                                                        {jobStatus.successful || 0}
                                                    </div>
                                                    <div className="text-sm text-gray-600">Successful</div>
                                                </div>
                                                <div>
                                                    <div className="text-2xl font-bold text-yellow-600">
                                                        {jobStatus.skipped || 0}
                                                    </div>
                                                    <div className="text-sm text-gray-600">Skipped</div>
                                                </div>
                                                <div>
                                                    <div className="text-2xl font-bold text-red-600">
                                                        {jobStatus.failed || 0}
                                                    </div>
                                                    <div className="text-sm text-gray-600">Failed</div>
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {jobStatus.duration && (
                                        <div className="text-sm text-gray-600">
                                            Duration: {formatDuration(jobStatus.duration)}
                                        </div>
                                    )}

                                    {jobStatus.status === 'completed' && (
                                        <div className="pt-4">
                                            <button
                                                onClick={() => navigate('/admin/products')}
                                                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                                            >
                                                View Products
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Instructions */}
                        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <h3 className="text-sm font-medium text-blue-900 mb-2">Instructions:</h3>
                            <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                                <li>Upload an Excel file with a "Saleable" sheet</li>
                                <li>The file should contain product data with columns: product_name, Item (category), variant_mrp, brand, etc.</li>
                                <li>Products will be processed one by one in the background</li>
                                <li>Existing products (by name) will be skipped</li>
                                <li>Images will be automatically fetched for new products</li>
                                <li>You can monitor the progress in real-time</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UploadExcelPage;

